import "./sentry"
import { registerSW } from "virtual:pwa-register"

import React from "react"
import { createRoot } from "react-dom/client"
import { Provider } from "react-redux"
import App from "./App"
import "./index.css"
import { persistor, store } from "./app/store"
import { PersistGate } from "redux-persist/integration/react"
import { ChakraProvider, ColorModeScript, extendTheme } from "@chakra-ui/react"
import { AvatarEditorModalProvider } from "./features/character/useAvatarEditorModal"
import { BrowserRouter } from "react-router-dom"
import { PERF_ENABLED, perfRegisterStateGetter } from "./perf/perfTrace"

// Register the service worker and reload once when a new build takes
// control (autoUpdate mode): an open page must never keep running old code
// whose asset names the new precache no longer has. The store flushes the
// saved game on pagehide first, so nothing is lost by the reload.
registerSW({ immediate: true })

if (PERF_ENABLED) perfRegisterStateGetter(() => store.getState().game)

const container = document.getElementById("root")

if (container) {
  const root = createRoot(container)

  const colors = {
    brand: {
      900: "#1a365d",
      800: "#153e75",
      700: "#2a69ac",
    },
    pink: {
      50: "#fdf2f8",
      100: "#fce7f3",
      200: "#fbcfe8",
      300: "#f9a8d4",
      400: "#f472b6",
      500: "#ec4899",
      600: "#db2777",
      700: "#be185d",
      800: "#9f1239",
      900: "#831843",
    },
  }

  const config = {
    initialColorMode: "light",
    useSystemColorMode: false,
  }

  const theme = extendTheme({
    colors,
    config,
    styles: {
      global: (props: { colorMode: string }) => ({
        body: {
          bg: props.colorMode === "dark" ? "gray.800" : "white",
          color: props.colorMode === "dark" ? "white" : "gray.800",
        },
      }),
    },
  })

  root.render(
    <React.StrictMode>
      <ColorModeScript initialColorMode={theme.config.initialColorMode} />
      <ChakraProvider theme={theme}>
        <Provider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <AvatarEditorModalProvider>
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </AvatarEditorModalProvider>
          </PersistGate>
        </Provider>
      </ChakraProvider>
    </React.StrictMode>,
  )
} else {
  throw new Error(
    "Root element with ID 'root' was not found in the document. Ensure there is a corresponding HTML element with the ID 'root' in your HTML file.",
  )
}
